import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Step {
  label: string;
  shortLabel: string;
}

const steps: Step[] = [
  { label: 'Contact Info', shortLabel: 'Contact' },
  { label: 'Organization', shortLabel: 'Org' },
  { label: 'Campaign Goals', shortLabel: 'Goals' },
  { label: 'Details & Timeline', shortLabel: 'Details' },
];

interface ProgressIndicatorProps {
  currentStep: number;
  completedSteps: number[];
  onStepClick: (step: number) => void;
}

export function ProgressIndicator({ currentStep, completedSteps, onStepClick }: ProgressIndicatorProps) {
  return (
    <div className="w-full py-4 sm:py-6">
      <div className="flex items-center justify-center gap-1 sm:gap-2">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isComplete = completedSteps.includes(stepNumber);
          const isCurrent = currentStep === stepNumber;
          const isClickable = isComplete && !isCurrent;

          return (
            <div key={index} className="flex items-center">
              {/* Step Circle */}
              <button
                onClick={() => isClickable && onStepClick(stepNumber)}
                disabled={!isClickable}
                className={cn(
                  'relative flex flex-col items-center gap-1.5',
                  isClickable && 'cursor-pointer',
                  !isClickable && 'cursor-default'
                )}
              >
                <motion.div
                  className={cn(
                    'relative w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-300',
                    isComplete && 'bg-gold text-dark-bg',
                    isCurrent && 'bg-gold text-dark-bg',
                    !isComplete && !isCurrent && 'bg-muted border-2 border-border text-muted-foreground'
                  )}
                  animate={isCurrent ? { 
                    boxShadow: ['0 0 0 0 rgba(232, 180, 81, 0.4)', '0 0 0 8px rgba(232, 180, 81, 0)', '0 0 0 0 rgba(232, 180, 81, 0.4)']
                  } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {isComplete ? (
                    <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    stepNumber
                  )}
                </motion.div>
                <span className={cn(
                  'text-[10px] sm:text-xs font-medium whitespace-nowrap',
                  (isComplete || isCurrent) ? 'text-foreground' : 'text-muted-foreground'
                )}>
                  <span className="hidden sm:inline">{step.label}</span>
                  <span className="sm:hidden">{step.shortLabel}</span>
                </span>
              </button>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="w-8 sm:w-12 lg:w-16 h-0.5 mx-1 sm:mx-2 relative">
                  <div className="absolute inset-0 bg-border" />
                  <motion.div
                    className="absolute inset-0 bg-gold origin-left"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: completedSteps.includes(stepNumber) ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
